import React from 'react';
import { Link } from 'react-router-dom';
import Alert from 'src/shared/components/ui/alert';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAuthService from 'src/shared/hooks/useAuthService';
import { useProductVisibility } from 'src/shared/hooks/useProductLicensing';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AdminViews } from 'src/routes/routesConfig';
import { MANAGE_PRODUCTS_PARAM } from 'src/shared/data/manageProductsParam';
import { PRODUCT_NAMES, PRODUCT_RELEASE, ProductKey, ProductVisibilityOutcome, ReleasePolicy } from 'src/shared/types/engine/products';

type ProductVisibilityNoticeProps = {
    product: ProductKey;
};

/**
 * What the customer of this organisation does not see, or `null` when they see
 * what staff see. `null` too while the settings load.
 */
const noticeFor = (product: ProductKey, visibility: ProductVisibilityOutcome | undefined): string | null => {
    const name = PRODUCT_NAMES[product];

    if (visibility === undefined) {
        return null;
    } else if (visibility === ProductVisibilityOutcome.HIDDEN) {
        return `${name} is set to Hidden for this organisation. Its users cannot see ${name}.`;
    } else if (visibility === ProductVisibilityOutcome.DEFAULT && PRODUCT_RELEASE[product] === ReleasePolicy.MANUAL) {
        return `${name} is set to Upsell for this organisation. Its users see the upsell page, not these results.`;
    } else {
        return null;
    }
};

/**
 * Tells internal staff which setting a product has when the customer does
 * not see the results. Without it, the page looks the same in every mode.
 *
 * `StandardAppLayout` shows it on every page behind a `ProductGate`, for the
 * product of the gate, and `UpgradePage` on every upsell page. Customers see
 * upsell pages, so the notice shows to internal staff only.
 *
 * The link opens the products flyout of this organisation, so the user does
 * not have to find the row again.
 *
 * Severity is `warning`, not `info`: a customer who cannot see the results is
 * a task for someone, but nothing is broken.
 */
const ProductVisibilityNotice = ({ product }: ProductVisibilityNoticeProps) => {
    const { currentCustomerId } = useCustomer();
    const { isCurrentUserSuperAdmin, permissions } = useAuthService();
    const visibility = useProductVisibility(product);

    const message = noticeFor(product, visibility);
    // Customers see an upsell page, and the notice is for staff.
    if (!message || !permissions?.is_internal_user) {
        return <></>;
    }

    const manageProductsHref = `${navRoutesConfig[AdminViews.ORGANISATIONS] as string}?${MANAGE_PRODUCTS_PARAM}=${currentCustomerId}`;

    return (
        <Alert severity='warning'>
            <span data-testid='product-visibility-notice'>
                {message}
                {/* Withheld rather than shown broken: /admin/organisations is super-admin only. */}
                {isCurrentUserSuperAdmin && (
                    <>
                        {' '}
                        <Link to={manageProductsHref} className='underline font-medium'>
                            Change the visibility
                        </Link>
                    </>
                )}
            </span>
        </Alert>
    );
};

export default ProductVisibilityNotice;
